import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { PatientFormView } from '@/components/views/PatientFormView';
import { useAuthStore } from '@/lib/stores/authStore';
import { patientsApi } from '@/lib/api/patients';
import { medplumResourcesApi, type PatientResource } from '@/lib/api/medplum-resources';
import { getErrorMessage } from '@/lib/api/client';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type {
  PatientRegistrationData,
  Gender,
  FormGender,
  MaritalStatus,
  FormMaritalStatus,
  PersonCandidate,
} from '@/lib/types/patient';
import {
  FORM_GENDER_LABELS,
  FORM_MARITAL_STATUS_LABELS,
  calculateAge,
  calculateDobFromAge,
  getPatientNameFromFhir,
  getPhoneFromTelecom,
} from '@/lib/types/patient';
import { createComponentLogger } from '@/lib/utils/logger';
import { sanitizePhoneInput } from '@/lib/utils/phone';

const logger = createComponentLogger('PatientFormContent');

// Maximum realistic age used in validations (in years)
const MAX_REALISTIC_AGE = 150;

/**
 * Patient form validation schema - Enhanced with comprehensive validations
 */
const patientFormSchema = z.object({
  identifier: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 3, {
      message: 'Identifier must be at least 3 characters',
    }),
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(50, 'First name must not exceed 50 characters')
    .refine((val) => /^[a-zA-Z\s]+$/.test(val), {
      message: 'First name can only contain letters and spaces',
    }),
  lastName: z
    .string()
    .trim()
    .max(50, 'Last name must not exceed 50 characters')
    .optional()
    .refine((val) => !val || /^[a-zA-Z\s]+$/.test(val), {
      message: 'Last name can only contain letters and spaces',
    }),
  dateOfBirth: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const date = new Date(val);
      return !isNaN(date.getTime());
    }, 'Invalid date format')
    .refine((val) => {
      if (!val || val === '') return true;
      const date = new Date(val);
      const today = new Date();
      return date <= today;
    }, 'Date of birth cannot be in the future')
    .refine((val) => {
      if (!val || val === '') return true;
      const date = new Date(val);
      const minDate = new Date('1900-01-01');
      return date >= minDate;
    }, 'Date of birth must be after 1900')
    .refine((val) => {
      if (!val || val === '') return true;
      // Check for unrealistic age (over 150 years)
      const date = new Date(val);
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      const actualAge =
        monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate()) ? age - 1 : age;
      return actualAge <= MAX_REALISTIC_AGE;
    }, 'Age cannot exceed 150 years. Please verify the date of birth.'),
  gender: z.enum(['M', 'F', 'O'], {
    required_error: 'Gender is required',
    invalid_type_error: 'Please select a valid gender',
  }),
  maritalStatus: z.enum(['S', 'M', 'D', 'W']).optional(),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine((val) => {
      // Must be exactly 10 digits, no spaces or special characters
      return /^\d{10}$/.test(val);
    }, 'Phone number must be exactly 10 digits with no spaces or special characters'),
  address: z.string().max(500, 'Address must not exceed 500 characters').optional(),
  weight: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      const num = parseFloat(val);
      return !isNaN(num) && num >= 1 && num <= 500;
    }, 'Weight must be between 1 and 500 kg'),
  age: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      const num = parseInt(val, 10);
      return !isNaN(num) && num >= 0 && num <= 150;
    }, 'Age must be between 0 and 150 years'),
});

type PatientFormData = z.infer<typeof patientFormSchema>;

export interface PatientFormContentProps {
  patientId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
  /** Callback to switch to appointment scheduling when patient already exists */
  onSwitchToAppointment?: (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => void;
  /** Pre-fill the phone number field (used when switching from appointment search) */
  prefillPhone?: string;
}

/** Display label for a FHIR gender code, or undefined when unknown. */
function fhirGenderLabel(gender?: string): string | undefined {
  const formGender = ({ male: 'M', female: 'F', other: 'O' } as Record<string, FormGender>)[
    gender ?? ''
  ];
  return formGender ? FORM_GENDER_LABELS[formGender] : undefined;
}

/**
 * Answers given while reviewing a registration: `confirmNewPatient` after
 * seeing the patients already registered with the phone, then `linkPersonId`
 * or `confirmed` after seeing people at other organizations.
 */
type ReviewDecision = {
  confirmNewPatient?: boolean;
  linkPersonId?: string;
  confirmed?: boolean;
};

export function PatientFormContent({
  patientId,
  onSuccess,
  onCancel,
  isModal = false,
  onSwitchToAppointment,
  prefillPhone,
}: PatientFormContentProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const selectedOrganization = useAuthStore((state) => state.selectedOrganization);
  const patientIdentifierConfig = projectConfig?.identifierLabel
    ? { label: projectConfig.identifierLabel }
    : undefined;

  const isEditMode = !!patientId;

  // Fetch existing patient data in edit mode
  const { data: patientData, isLoading: isLoadingPatient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => patientsApi.getById(patientId!),
    enabled: isEditMode,
  });

  // Initialize form
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientFormSchema),
    mode: 'onBlur',
    defaultValues: {
      gender: 'M',
      maritalStatus: undefined,
      phoneNumber: prefillPhone || '',
      address: '',
    },
  });

  // Pre-fill phone number if provided (e.g., from appointment search)
  React.useEffect(() => {
    if (prefillPhone && !isEditMode) {
      setValue('phoneNumber', prefillPhone, { shouldValidate: true });
    }
  }, [prefillPhone, isEditMode, setValue]);

  // Populate form with existing patient data
  React.useEffect(() => {
    if (patientData?.patient) {
      const patient = patientData.patient;
      setValue('identifier', patient.identifier);
      setValue('firstName', patient.firstName || '');
      setValue('lastName', patient.lastName || '');
      setValue('dateOfBirth', patient.dateOfBirth.split('T')[0]);
      // Only set gender if it's M, F, or O (form doesn't show Unknown option)
      // If patient has Unknown gender, default to Male
      if (Object.keys(FORM_GENDER_LABELS).includes(patient.gender)) {
        setValue('gender', patient.gender as FormGender);
      }
      // Set marital status if available and valid
      if (
        patient.maritalStatus &&
        Object.keys(FORM_MARITAL_STATUS_LABELS).includes(patient.maritalStatus)
      ) {
        setValue('maritalStatus', patient.maritalStatus as FormMaritalStatus);
      }
      setValue('phoneNumber', sanitizePhoneInput(patient.phoneNumber));
      // Set address if available
      if (patient.address) {
        setValue('address', patient.address);
      }
    }
  }, [patientData, setValue]);

  // ========== Phone Search (for create mode in modal) ==========
  // State for phone-based patient search - supports multiple patients per phone
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [foundPatients, setFoundPatients] = useState<
    Array<{
      id: string;
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      gender?: 'M' | 'F' | 'O' | 'U';
    }>
  >([]);
  const lastSearchedPhone = useRef<string>('');

  // ========== Identifier Search (for create mode in modal, when configured) ==========
  const [isSearchingByIdentifier, setIsSearchingByIdentifier] = useState(false);
  const [foundPatientsByIdentifier, setFoundPatientsByIdentifier] = useState<
    Array<{
      id: string;
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      gender?: 'M' | 'F' | 'O' | 'U';
    }>
  >([]);
  const lastSearchedIdentifier = useRef<string>('');

  // ========== Cross-org identity review (front desk decides after a create attempt) ==========
  // Set only when POST /v2/patients comes back with `needsReview: true` — the
  // patient was NOT created yet. Holds the exact form data used for that
  // attempt so it can be re-submitted once a decision is made.
  const [reviewState, setReviewState] = useState<{
    data: PatientFormData;
    /** Patients of this organization already registered with the phone. */
    existingPatients?: PatientResource[];
    /** People with the phone at other organizations. */
    candidates?: PersonCandidate[];
    /** Answers given so far, re-sent with the same data on every attempt. */
    decision: ReviewDecision;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Watch phone number for auto-search
  const phoneNumber = watch('phoneNumber');
  // Watch identifier for auto-search
  const identifierValue = watch('identifier');
  const debouncedIdentifier = useDebounce(identifierValue || '', 500);

  // Look up patients already registered with this phone number as soon as it is
  // complete (create mode), so the same person is not registered twice by
  // mistake and an existing patient can be picked instead.
  useEffect(() => {
    if (isEditMode) return;

    // Anything but a complete number clears the list and re-arms the search,
    // so typing the same number again later searches again.
    if (phoneNumber?.length !== 10) {
      lastSearchedPhone.current = '';
      setFoundPatients([]);
      return;
    }

    // Don't re-search the number already searched
    if (phoneNumber === lastSearchedPhone.current) {
      return;
    }
    lastSearchedPhone.current = phoneNumber;

    let cancelled = false;
    const searchPatients = async () => {
      setIsSearchingPatient(true);

      try {
        logger.log('Auto-searching patients by phone:', phoneNumber);
        const patients = await medplumResourcesApi.searchPatientByPhone(phoneNumber);
        if (cancelled) return;

        setFoundPatients(
          patients.map((patient) => {
            const name = patient.name?.[0];
            let gender: 'M' | 'F' | 'O' | 'U' = 'M';
            if (patient.gender === 'female') gender = 'F';
            else if (patient.gender === 'other') gender = 'O';
            else if (patient.gender === 'unknown') gender = 'U';

            return {
              id: patient.id!,
              firstName: name?.given?.[0] || '',
              lastName: name?.family || '',
              dateOfBirth: patient.birthDate || '',
              gender,
            };
          })
        );
        logger.log(`Found ${patients.length} patient(s) for phone:`, phoneNumber);
      } catch (error) {
        if (cancelled) return;
        logger.error('Error searching patient by phone:', error);
        setFoundPatients([]);
        // Allow another attempt, and don't let a failed search pass as "nobody found".
        lastSearchedPhone.current = '';
        toast.error('Could not check for existing patients', {
          description: getErrorMessage(error),
          id: 'phone-search-failed',
        });
      } finally {
        if (!cancelled) setIsSearchingPatient(false);
      }
    };

    searchPatients();
    return () => {
      cancelled = true;
    };
  }, [phoneNumber, isEditMode]);

  // Auto-search by identifier when it reaches 3+ chars (only in create modal, when configured)
  useEffect(() => {
    if (isEditMode || !isModal || !patientIdentifierConfig) return;

    if (!debouncedIdentifier || debouncedIdentifier.length < 3) {
      if (foundPatientsByIdentifier.length > 0) {
        setFoundPatientsByIdentifier([]);
      }
      // Reset the last searched identifier so re-entering the same value triggers a new search
      lastSearchedIdentifier.current = '';
      return;
    }

    // Don't re-search the same identifier
    if (debouncedIdentifier === lastSearchedIdentifier.current) {
      return;
    }

    const searchByIdentifier = async () => {
      setIsSearchingByIdentifier(true);
      try {
        logger.log('Auto-searching patients by identifier:', debouncedIdentifier);
        const patients = await medplumResourcesApi.searchPatientByIdentifier(debouncedIdentifier);
        lastSearchedIdentifier.current = debouncedIdentifier;

        if (patients.length > 0) {
          const mapped = patients.map((patient) => {
            const name = patient.name?.[0];
            let gender: 'M' | 'F' | 'O' | 'U' = 'M';
            if (patient.gender === 'female') gender = 'F';
            else if (patient.gender === 'other') gender = 'O';
            else if (patient.gender === 'unknown') gender = 'U';
            return {
              id: patient.id!,
              firstName: name?.given?.[0] || '',
              lastName: name?.family || '',
              dateOfBirth: patient.birthDate || '',
              gender,
            };
          });
          setFoundPatientsByIdentifier(mapped);
          logger.log(`Found ${mapped.length} patient(s) for identifier:`, debouncedIdentifier);
        } else {
          setFoundPatientsByIdentifier([]);
        }
      } catch (error) {
        logger.error('Error searching patient by identifier:', error);
        setFoundPatientsByIdentifier([]);
      } finally {
        setIsSearchingByIdentifier(false);
      }
    };

    searchByIdentifier();
  }, [debouncedIdentifier, isEditMode, isModal, patientIdentifierConfig]);

  // Reset found patients when explicitly clearing
  const clearFoundPatients = useCallback(() => {
    setFoundPatients([]);
    lastSearchedPhone.current = '';
  }, []);

  // Continues the post-creation flow (transition to appointment booking, close
  // the modal, or navigate to the dashboard). Shared by the immediate success
  // path and the deferred path after a front-desk review decision.
  function finalizeAfterCreate(p: PatientResource) {
    if (isModal && onSwitchToAppointment) {
      const phone = getPhoneFromTelecom(p.telecom as any);
      const genderMap: Record<string, 'M' | 'F' | 'O' | 'U'> = {
        male: 'M',
        female: 'F',
        other: 'O',
        unknown: 'U',
      };
      onSwitchToAppointment({
        id: p.id!,
        name: getPatientNameFromFhir(p.name as any) || 'Patient',
        phoneNumber: phone === 'N/A' ? '' : phone,
        age: p.birthDate ? calculateAge(p.birthDate) : undefined,
        gender: genderMap[p.gender ?? ''] ?? 'U',
      });
    } else if (isModal && onSuccess) {
      onSuccess();
    } else {
      navigate('/dashboard');
    }
  }

  // Create mutation - creates patient only (no appointment)
  // Multiple patients can share the same phone number (e.g., family members)
  const createMutation = useMutation({
    mutationFn: async (params: { data: PatientFormData; decision?: ReviewDecision }) => {
      if (!user) throw new Error('User not authenticated');
      const { data, decision } = params;

      logger.log('Creating new patient in Medplum with phone:', data.phoneNumber);

      // If no DOB provided but age is, calculate an approximate DOB from age
      const parsedAge = data.age ? parseInt(data.age, 10) : NaN;
      const resolvedDob =
        data.dateOfBirth || (!isNaN(parsedAge) ? calculateDobFromAge(parsedAge) : undefined);

      const result = await medplumResourcesApi.createPatient({
        firstName: data.firstName,
        lastName: data.lastName || '',
        dateOfBirth: resolvedDob,
        gender:
          data.gender === 'M'
            ? 'male'
            : data.gender === 'F'
              ? 'female'
              : data.gender === 'O'
                ? 'other'
                : 'unknown',
        maritalStatus: data.maritalStatus,
        phone: data.phoneNumber,
        identifier: data.identifier,
        address: data.address,
        organizationId: selectedOrganization?.id,
        organizationName: selectedOrganization?.name,
        ...decision,
      });

      if (result.patient) logger.log('Patient created:', result.patient.id);
      return result;
    },
    onSuccess: (response, variables) => {
      // A phone match was found and no decision has been made yet — hold off
      // on navigating away; show the candidates and wait for the front desk.
      if (response.needsReview) {
        setReviewState({
          data: variables.data,
          existingPatients: response.existingPatients,
          candidates: response.candidates,
          decision: variables.decision ?? {},
        });
        return;
      }

      setReviewState(null);

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['patients'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['dashboard-patients'], exact: false });

      const patient = response.patient as PatientResource;
      const patientName = getPatientNameFromFhir(patient.name as any) || 'Patient';
      if (response.linked) {
        toast.success('Patient Created & Linked', {
          description: `${patientName} has been registered and linked to the existing record.`,
        });
      } else {
        toast.success('Patient Created!', {
          description: `${patientName} has been registered successfully.`,
        });
      }

      finalizeAfterCreate(patient);
    },
    onError: (error) => {
      logger.error('Failed to create patient:', error);
      toast.error('Failed to create patient', {
        description: getErrorMessage(error),
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: PatientRegistrationData) => patientsApi.update(patientId!, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['patients'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['patient', patientId], exact: false });
      toast.success('Patient updated successfully', {
        description: `${response.patient.name} has been updated.`,
      });
      if (isModal && onSuccess) {
        onSuccess();
      } else {
        navigate(`/patient-record/${patientId}`);
      }
    },
    onError: (error) => {
      toast.error('Failed to update patient', {
        description: getErrorMessage(error),
      });
    },
  });

  // Handle form submission
  const onSubmit = async (data: PatientFormData) => {
    if (isEditMode) {
      const patientData: PatientRegistrationData = {
        identifier: data.identifier,
        firstName: data.firstName,
        lastName: data.lastName || '',
        dateOfBirth: data.dateOfBirth,
        gender: data.gender as Gender,
        maritalStatus: data.maritalStatus as MaritalStatus | undefined,
        phoneNumber: data.phoneNumber,
        address: data.address,
        organizationId: selectedOrganization?.id,
        organizationName: selectedOrganization?.name,
      };
      await updateMutation.mutateAsync(patientData);
    } else {
      await createMutation.mutateAsync({ data });
    }
  };

  const handleCancel = () => {
    if (isModal && onCancel) {
      onCancel();
    } else {
      navigate(isEditMode ? `/patient-record/${patientId}` : '/dashboard');
    }
  };

  // Re-submit the same data with one more answer baked in. Once nothing is left
  // to review, this actually creates the chart.
  const submitDecision = async (answer: ReviewDecision) => {
    if (!reviewState) return;
    setIsConfirming(true);
    try {
      await createMutation.mutateAsync({
        data: reviewState.data,
        decision: { ...reviewState.decision, ...answer },
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Front desk picked one of the other-organization candidates: same person.
  const handleLinkToCandidate = (personId: string) => submitDecision({ linkPersonId: personId });

  // Front desk said "none of these": create a new person.
  const handleCreateNewPerson = () => submitDecision({ confirmed: true });

  // The patient is one of those already registered here: use them instead of
  // registering again.
  const handleUseExistingPatient = (patient: PatientResource) => {
    setReviewState(null);
    if (isModal && (onSwitchToAppointment || onSuccess)) {
      finalizeAfterCreate(patient);
    } else {
      navigate(`/patient-record/${patient.id}`);
    }
  };

  // Someone else shares the number (e.g. a family member): register them too.
  const handleRegisterAnother = () => submitDecision({ confirmNewPatient: true });

  const values = {
    firstName: watch('firstName') || '',
    lastName: watch('lastName') || '',
    dateOfBirth: watch('dateOfBirth') || '',
    gender: (watch('gender') || 'M') as Gender,
    maritalStatus: watch('maritalStatus') as MaritalStatus | undefined,
    phoneNumber: watch('phoneNumber') || '',
    address: watch('address') || '',
    identifier: watch('identifier') || '',
    weight: watch('weight') || '',
    age: watch('age') || '',
  };

  // Merge and deduplicate patients found by phone and by identifier
  const allFoundPatients = React.useMemo(() => {
    const combined = [...foundPatients];
    for (const p of foundPatientsByIdentifier) {
      if (!combined.some((existing) => existing.id === p.id)) {
        combined.push(p);
      }
    }
    return combined;
  }, [foundPatients, foundPatientsByIdentifier]);

  // Handle switching to appointment scheduling for a specific existing patient
  const handleSwitchToAppointment = (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => {
    if (onSwitchToAppointment) {
      onSwitchToAppointment(patient);
    }
  };

  // A phone match was found on submit and nothing was created yet. First the
  // patients of this organization already registered with the number, so the
  // same person is not registered twice; then people at other organizations,
  // to link to or not. This replaces the form until a decision is made.
  if (reviewState?.existingPatients && reviewState.existingPatients.length > 0) {
    return (
      <div className="mx-auto max-w-md rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
        <p className="font-medium text-amber-900">
          {reviewState.existingPatients.length}{' '}
          {reviewState.existingPatients.length > 1 ? 'patients are' : 'patient is'} already
          registered with this number
        </p>
        <p className="mt-0.5 text-xs text-amber-800">
          Is this one of them? Use the existing patient, or register someone else who shares the
          number.
        </p>
        <div className="mt-2 space-y-1.5">
          {reviewState.existingPatients.map((patient) => {
            const name = getPatientNameFromFhir(patient.name as any) || 'Unknown Patient';
            const details = [
              fhirGenderLabel(patient.gender),
              patient.birthDate ? `${calculateAge(patient.birthDate)} yrs` : undefined,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={patient.id}
                className="flex items-center justify-between gap-2 rounded-md bg-white p-2 shadow-sm"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{name}</span>
                  {details && <span className="text-xs text-gray-500">{details}</span>}
                </div>
                <button
                  type="button"
                  disabled={isConfirming}
                  onClick={() => handleUseExistingPatient(patient)}
                  className="rounded-md bg-[#061a61] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#061a61]/90 disabled:opacity-50"
                >
                  Use this patient
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={isConfirming}
            onClick={() => setReviewState(null)}
            className="flex-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50"
          >
            Back to form
          </button>
          <button
            type="button"
            disabled={isConfirming}
            onClick={handleRegisterAnother}
            className="flex-1 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50"
          >
            {isConfirming ? 'Checking…' : 'Register someone else'}
          </button>
        </div>
      </div>
    );
  }

  if (reviewState?.candidates && reviewState.candidates.length > 0) {
    const { candidates } = reviewState;
    return (
      <div className="mx-auto max-w-md rounded-md border border-blue-200 bg-blue-50 p-4 text-sm">
        <p className="font-medium text-blue-900">
          {candidates.length} other {candidates.length > 1 ? 'people' : 'person'} registered with
          this number at other organizations
        </p>
        <p className="mt-0.5 text-xs text-blue-800">
          If this patient is one of them, link to that person. Otherwise create a new record.
        </p>
        <div className="mt-2 space-y-1.5">
          {candidates.map((candidate) => {
            const orgNames = candidate.organizations
              .map((o) => o.display || o.reference)
              .filter(Boolean)
              .join(', ');
            const details = [candidate.gender, candidate.birthDate].filter(Boolean).join(' · ');
            return (
              <div
                key={candidate.personId}
                className="flex items-center justify-between gap-2 rounded-md bg-white p-2 shadow-sm"
              >
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{candidate.name || 'Unknown'}</span>
                  {details && <span className="text-xs text-gray-500">{details}</span>}
                  {orgNames && <span className="text-xs text-gray-500">{orgNames}</span>}
                </div>
                <button
                  type="button"
                  disabled={isConfirming}
                  onClick={() => handleLinkToCandidate(candidate.personId)}
                  className="rounded-md bg-[#061a61] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#061a61]/90 disabled:opacity-50"
                >
                  {isConfirming ? 'Linking…' : 'Link'}
                </button>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          disabled={isConfirming}
          onClick={handleCreateNewPerson}
          className="mt-3 w-full rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-900 transition-colors hover:bg-blue-100 disabled:opacity-50"
        >
          {isConfirming ? 'Creating…' : 'None of these — create new record'}
        </button>
      </div>
    );
  }

  return (
    <PatientFormView
      mode={isEditMode ? 'edit' : 'create'}
      values={values}
      errors={{
        firstName: errors.firstName?.message,
        lastName: errors.lastName?.message,
        dateOfBirth: errors.dateOfBirth?.message,
        gender: errors.gender?.message,
        maritalStatus: errors.maritalStatus?.message,
        phoneNumber: errors.phoneNumber?.message,
        identifier: errors.identifier?.message,
        weight: errors.weight?.message,
        age: errors.age?.message,
      }}
      options={{
        gender: [
          { value: 'M', label: FORM_GENDER_LABELS.M },
          { value: 'F', label: FORM_GENDER_LABELS.F },
          { value: 'O', label: FORM_GENDER_LABELS.O },
        ],
        maritalStatus: [
          { value: 'S', label: FORM_MARITAL_STATUS_LABELS.S },
          { value: 'M', label: FORM_MARITAL_STATUS_LABELS.M },
          { value: 'D', label: FORM_MARITAL_STATUS_LABELS.D },
          { value: 'W', label: FORM_MARITAL_STATUS_LABELS.W },
        ],
      }}
      loading={{
        isSubmitting,
        isLoadingPatient,
        isSearchingPatient: isSearchingPatient || isSearchingByIdentifier,
      }}
      isFormValid={isValid}
      existingPatients={
        allFoundPatients.length > 0
          ? allFoundPatients.map((p) => ({
              id: p.id,
              name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown Patient',
              age: p.dateOfBirth ? calculateAge(p.dateOfBirth) : undefined,
              gender: (p.gender || 'M') as 'M' | 'F' | 'O' | 'U',
              phoneNumber: values.phoneNumber,
            }))
          : undefined
      }
      phoneSearchMode={isModal && !isEditMode}
      onSwitchToAppointment={onSwitchToAppointment ? handleSwitchToAppointment : undefined}
      identifierLabel={patientIdentifierConfig?.label}
      organizationName={selectedOrganization?.name}
      onAgeChange={(age) => {
        const ageStr = age !== null && age !== undefined && !isNaN(age) ? age.toString() : '';
        setValue('age', ageStr, { shouldValidate: true });
      }}
      onChange={(field, value) => {
        // If phone is being changed, reset found patients
        if (
          field === 'phoneNumber' &&
          (foundPatients.length > 0 || foundPatientsByIdentifier.length > 0)
        ) {
          clearFoundPatients();
        }
        // If identifier is being changed, reset identifier-found patients
        if (field === 'identifier' && foundPatientsByIdentifier.length > 0) {
          setFoundPatientsByIdentifier([]);
        }
        if (field === 'gender' && typeof value === 'string') {
          if (Object.keys(FORM_GENDER_LABELS).includes(value)) {
            setValue('gender', value as 'M' | 'F' | 'O', { shouldValidate: true });
          }
        } else if (field === 'maritalStatus' && typeof value === 'string') {
          if (Object.keys(FORM_MARITAL_STATUS_LABELS).includes(value) || value === '') {
            setValue('maritalStatus', value === '' ? undefined : (value as 'S' | 'M' | 'D' | 'W'), {
              shouldValidate: true,
            });
          }
        } else {
          setValue(field as keyof PatientFormData, value as never, {
            shouldValidate: true,
          });
        }
      }}
      onCancel={handleCancel}
      onSubmit={() => handleSubmit(onSubmit)()}
      actionLabels={{
        submit: isEditMode ? 'Update Patient' : 'Add Patient',
        cancel: 'Cancel',
      }}
    />
  );
}
